import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DbServerErrors} from "../../shared/interceptors/db-server-errors";
import {AuthService} from "../auth.service";
import {DbResponse} from "../../shared/interceptors/db-response";
import {TypeUtils} from "../../shared/utils/type-utils";
import {Utils} from "../../shared/utils/utils";
import {Location} from '@angular/common';
import {DbValidators} from "../../shared/validators/DbValidators";
import {UserService} from '../../user/user.service';
import {UserSubscription, UserSubscriptionIds, UserSubscriptionType} from "../../user/user-subscription";
import {Store} from "@ngrx/store";
import * as fromStore from '../../store';
import {combineLatest, Subscription} from 'rxjs';
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Website} from "../../websites/website/website";
import {GlobalEvent} from "../../shared/services/global-event-dispatcher/global-event";
import {
  GlobalEventDispatcherService
} from "../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {ReCaptchaV3Service} from "ngx-captcha";
import {Config} from "../../config/config";
import {CookieService} from "ngx-cookie";
import {User} from "../../user/user";
import {UserManagementService} from "../../user-managment/user-management.service";
import {NotifierComponent} from "../../shared/components/notifier/notifier.component";
import {Notification} from "../../shared/components/notifier/notification";
import {NotificationType} from "../../shared/components/notifier/notification-type";
import {ExpertsService} from "../../services/API/experts.service";
import {PluginConnectService} from "../../shared/services/plugin-connect/plugin-connect.service";
import {GoogleAnalyticsService} from "../../shared/services/google-analytics/google-analytics.service";
import {CheckoutService} from "../checkout/checkout.service";
import {BrandingService} from "../../shared/services/branding/branding.service";
import {CompanyInfo} from "../../store/actions/company-info.action";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'db-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {
  private token: string;
  private email: string;
  private emailFromMail: string;
  public loading: boolean;
  public _2faVerificationStep: boolean = false;
  public hasServerFieldErrors: boolean;
  public loginForm: FormGroup;
  public serverErrors: DbServerErrors;
  public formSubmitted: boolean = false;
  public userNotVerified: boolean = false;
  public subscriptionId: number;
  public pluginSlug: string;
  public notActivated: boolean;
  public loggedIn: boolean;
  public answersDataString: string;
  public verified: boolean = false;
  public websites: Website[];
  public userSubscription: UserSubscription;

  public isLoginFromManager: boolean = false;
  public _isLoginFromZendesk: boolean;
  public showPassword: boolean = false;
  public authData: any;
  public magicData: any;
  public googleError: any;
  public loadingGoogle: any;

  public enableRecaptcha: boolean;
  public fromResetPassword: boolean;
  public magicLinkPath: string;
  public loadingMagicLink: boolean;
  public userSubscriptionIds: any = UserSubscriptionIds;
  public invitationData: any;
  public authKeyFromGoogle: any;
  public fromExperts: string;
  public fromGallery: boolean = false;
  public isMobile: boolean = false;
  public userLocalStorage: any = {};
  public showPercentOffer: boolean = false;
  public endDate: any;
  public googleLoading: boolean = false;
  public loginWithGoogle: boolean = false;
  public twoFaToken: string;
  public googleToken: string;
  public companyInfo: CompanyInfo;
  public hasStarterTrialSubscription: boolean = false;
  private _destroyRef = inject(DestroyRef);
  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _location: Location,
    private _authService: AuthService,
    private _userService: UserService,
    private _store: Store<fromStore.DbStoreState>,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private _cookieService: CookieService,
    private _userManagementService: UserManagementService,
    private _expertsService: ExpertsService,
    private _pluginConnectService: PluginConnectService,
    private readonly _googleAnalyticsService: GoogleAnalyticsService,
    private readonly _checkoutService: CheckoutService,
    private readonly _brandingService: BrandingService,
  ) {
    this.loginForm = new FormGroup({
      'email': new FormControl('', [DbValidators.trim, Validators.required, Validators.email, Validators.pattern(/^[+_\-a-zA-Z0-9]+(\.[+_\-a-zA-Z0-9]+)*@[+_\-a-zA-Z0-9]+(\.[+_\-A-Za-z0-9]+)*(\.[+_\-A-Za-z]{2,24})$/)]),
      'password': new FormControl('', [Validators.required, DbValidators.trim])
    });
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 991;
    this.isLoginFromManager = !!this._pluginConnectService.pluginData;
    this.recaptchaMode();
    if (typeof this._cookieService.get('db-user-google-sign-up') !== 'undefined') {
      localStorage.setItem('loadingGoogle', '1');
    }
    this.loadingGoogle = localStorage.getItem('loadingGoogle');
    if (localStorage.getItem('dbAnswersData')) {
      this.answersDataString = localStorage.getItem('dbAnswersData');
    }
    const queryParams: any = (<any>this._route.queryParams).value;
    if (queryParams && queryParams.email && queryParams.auth_key) {
      this.loginForm.get('email').setValue(queryParams.email);
      this.authKeyFromGoogle = queryParams.auth_key;
    }
    if (window.location.href.indexOf("fromExperts") > -1) {
      this.fromExperts = new URL(window.location.href).searchParams.get("fromExperts");
    }
    this.invitationData = queryParams.invitation_data || new URL(window.location.href).searchParams.get("invitation_data");
    if (this.invitationData) {
      this.invitationData = Utils.b64DecodeUnicode(this.invitationData);
      this.loginForm.get('email').setValue(this.invitationData.client_email);
    }
    if (queryParams.testing) {
      this._cookieService.put('db-testing', '1', {path: '/', domain: '10web.io'});
    }
    const subscriptionIdFromLocal = parseInt(localStorage.getItem('subscriptionId'), 10) === 261 || parseInt(localStorage.getItem('subscriptionId'), 10) === 211 ? Config.basePlanId : parseInt(localStorage.getItem('subscriptionId'), 10);
    this.subscriptionId = parseInt(queryParams && queryParams.subscr_id ? queryParams.subscr_id : (subscriptionIdFromLocal ? subscriptionIdFromLocal : Config.basePlanId), 10);
    this.userNotVerified = queryParams.userNotVerified ? Utils.isTruthy(queryParams.userNotVerified) : false;
    this.pluginSlug = queryParams.plugin_slug;
    this.notActivated = Utils.isTruthy(queryParams.notActivated);
    this.token = queryParams.token;
    this.email = queryParams.email;
    this.authData = queryParams.authdata || '';
    this.magicData = queryParams.magic_data || '';
    this.fromGallery = !this.magicData && queryParams.login_request_plugin === 'photo_gallery';
    this.fromResetPassword = queryParams.fromResetPassword;
    this.magicLinkPath = queryParams?.returnUrl?.includes('emailCampaign') && queryParams.returnUrl;
    this.emailFromMail = queryParams?.returnUrl?.includes('email') && new URLSearchParams(queryParams.returnUrl.split('?')[1]).get('email');
    const queryParamsIndex: number = this._router.url.indexOf('?');
    this.initLoginFromPlugin();
    if (this.magicData) {
      this.loadingGoogle = true;
      this.magicData = Utils.b64DecodeUnicode(this.magicData);
      if (this.magicData.magic_token && this.magicData.email) {
        const token = this.magicData.magic_token;
        const email = this.magicData.email;
        if (this.enableRecaptcha) {
          this.reCaptchaV3Service.execute(Config.googleRecaptcha, 'signup', (recaptchaToken) => {
            this.loginFromMagicLink(token, email, recaptchaToken);
          });
        } else {
          this.loginFromMagicLink(token, email);
        }
      }
      else {
        this.loadingGoogle = false;
      }
    }
    else {
      if ( this.authData ) {
        this._cookieService.remove("db-user-google-sign-up", {path: '/', domain: '10web.io'});
        this.authData = Utils.b64DecodeUnicode(this.authData);
        if (this.authData.status === 'ok') {
          if (this.authData.data && this.authData.data.tenweb_cookie) {
            this._cookieService.put('FPID', this.authData.data.tenweb_cookie, {path: '/', domain: '10web.io'});
          }
          if (this.authData.data && this.authData.data.dashboard_token) {
            localStorage.setItem('dbUserAccessToken', this.authData.data.dashboard_token.access_token);
            localStorage.setItem('dbUserRefreshToken', this.authData.data.dashboard_token.refresh_token);
            localStorage.setItem('dbCurrentWorkspaceId', (this.authData.data.current_workspace_id || this.authData.data.workspace_id).toString());
            localStorage.setItem('dbOwnWorkspaceId', this.authData.data.workspace_id.toString());
            localStorage.setItem('dbCurrentWorkspaceRoleId', this.authData.data.role_id.toString());
            this._store.dispatch(new fromStore.SetWorkspaceIdEntities(
              {
                ownerWorkspaceId: this.authData.data.workspace_id,
                currentWorkspaceId: this.authData.data.current_workspace_id || this.authData.data.workspace_id,
                currentWorkspaceRoleId: this.authData.data.role_id,
                globalEventDispatcherService: this._globalEventDispatcher
              }
            ));
            this._globalEventDispatcher.dispatchEvent(GlobalEvent.userLogIn);
            this._store.dispatch(new fromStore.UserLogIn);

            if (this.authData.data
              && (this.authData.data.subscription_id === Config.defaultStarterPlanId
                || this.authData.data.subscription_id === Config.speedStarterPlanId
                || this.authData.data.subscription_id === Config.basePlanId)
            ) {

              if (this.answersDataString && this.answersDataString !== '') {
                localStorage.removeItem("dbAnswersData");
                const answersData = this.answersDataString;
                this.updateSignUpForData(JSON.parse(answersData));
              }
              else {
                this._router.navigate(['/setup'], {
                  queryParams: {
                    fromGoogleSignIn: true,
                    subscr_id: this.authData.data.subscription_id
                  }
                });
              }
            }
            else if (this.authData.data
              && (this.authData.data.subscription_id === this.userSubscriptionIds.TRIAL_OPTIMIZER
                || this.authData.data.subscription_id === this.userSubscriptionIds.TRIAL)
            ) {
              this._router.navigate(['/add-website'], {
                queryParams: {
                  fromGoogleSignIn: true,
                  subscr_id: this.authData.data.subscription_id,
                }
              });
            }
            else if (this.authData.data.subscription_id === this.userSubscriptionIds.TRIAL
              && localStorage.getItem('forceCreateWebsiteRegion') || this.authData.data.zone) {
              const region = localStorage.getItem('forceCreateWebsiteRegion') || this.authData.data.zone;
              localStorage.removeItem('forceCreateWebsiteRegion');
              this._router.navigate(['/websites'], {
                queryParams: {
                  region: region,
                  forceCreateWebsite: true,
                  fromGoogleSignIn: true,
                  fromSignup: this.authData && this.authData.data.from_sign_up,
                  subscr_id: this.authData.data.subscription_id,
                }
              });
            }
            else {
              this._router.navigate(['/websites'], {
                queryParams: {
                  fromSignup: this.authData && this.authData.data.from_sign_up,
                  fromGoogleSignIn: true,
                  subscr_id: this.authData.data.subscription_id,
                  fromSignUpTrial: this.authData.data.subscription_id === this.userSubscriptionIds.TRIAL,
                }
              });
            }
            localStorage.removeItem("loadingGoogle");
            localStorage.removeItem('forceCreateWebsiteRegion');
          }
          else {
            this.loadingGoogle = false;
            localStorage.removeItem("loadingGoogle");
            localStorage.removeItem('forceCreateWebsiteRegion');
          }
        }
        else if (this.authData.status === 'error') {
          this.loadingGoogle = false;
          this.googleError = this.authData.message;
          if (this.authData.data && this.authData.data.email && this.authData.data.auth_key) {
            this.loginForm.get('email').setValue(this.authData.data.email);
            this.authKeyFromGoogle = this.authData.data.auth_key;
          }
          if ( this.serverErrors ) {
            this.serverErrors.message = this.authData.message;
          }
        }
        else if (this.authData.error) {
          this.loadingGoogle = false;
          this.googleError = this.authData.error.message;
          if ( this.serverErrors ) {
            this.serverErrors.message = this.authData.error.message;
          }
        }
        else {
          this.loadingGoogle = false;
          localStorage.removeItem("loadingGoogle");
          this._pluginConnectService.resetPluginData();
        }
      }
      localStorage.removeItem('forceCreateWebsiteRegion');
      localStorage.removeItem("loadingGoogle");
    }
    this.initLoginFromZendesk(queryParams);

    this._router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const queryParamsnew: any = (<any>this._route.queryParams).value;
        if ( this._router.url.indexOf('?') !== -1) {
          this.fromResetPassword = queryParamsnew.fromResetPassword;
          this._location.replaceState(this._router.url.substring(0,  this._router.url.indexOf('?')));
        }
      });

    if (queryParamsIndex !== -1 && !this.fromResetPassword) {
      this._location.replaceState(this._router.url.substring(0, queryParamsIndex));
    }
    this._setCompanyInfoFromRoute();
    this._checkStarterTrialAvailability();
    this._showPercentOffer();
  }

  private _checkStarterTrialAvailability(): void {
      this._store.select(fromStore.getUserAvailableSubscriptionsLoaded)
          .pipe(
              filter((loaded: boolean) => loaded),
              switchMap(() => this._store.select(fromStore.getUserAvailableHostingSubscriptions('platform'))),
              takeUntilDestroyed(this._destroyRef)
          ).subscribe((userAvailableSubscriptions: UserSubscription[]) => {
              this.hasStarterTrialSubscription = !!(userAvailableSubscriptions && userAvailableSubscriptions.length && userAvailableSubscriptions.some(
                  subscription => subscription.type === 'starter'));
          });
  }

  private _showPercentOffer() {
    const nowDate = new Date().getTime();
    const blackFridayEndDate = new Date("2023-11-28 12:00:00").getTime();
    this.showPercentOffer = nowDate < blackFridayEndDate;
    if (this.showPercentOffer) {
      const endDate = new Date("2023-11-28 12:00:00").getTime();
      this.endDate = endDate;
      const datesDiff = Utils.getTimeDiffFromToday(endDate);
      this.showPercentOffer = datesDiff;
    }
  }
  private updateSignUpForData(answersData): void {
    this._authService.updateSignUpFor(answersData, true)
      .pipe(
        first(response => !!response, null),
      )
      .subscribe(() => {
        this.updateUserLocalStorage(answersData);
      });
  }

  private updateUserLocalStorage(answersData): void {
    this.userLocalStorage['signUpFor'] = answersData;
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(
        first(response => !!response, null),
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
          this._store.dispatch(new fromStore.LoadUser());

          if (this.authData?.data?.product_id == Config.aiCreateWebsitePluginId)  {
            setTimeout(() => {
              this._router.navigate(['/setup/ai-convert']);
            }, 1000);
          }
          else  if (this.authData?.data?.product_id == Config.managerPluginId) {
            setTimeout(() => {
              this._router.navigate(['/setup/change-hosting']);
            }, 1000);
          }
          else  if (this.authData?.data?.product_id == Config.installBlankPluginId) {
            setTimeout(() => {
              this._router.navigate(['/setup/install-blank']);
            }, 1000);
          }
          else if (this.authData?.data?.subscription_id == this.userSubscriptionIds.TRIAL_BOOSTER) {
            setTimeout(() => {
              this._router.navigate(['/websites']);
            }, 1000);
          }
          else {
            setTimeout(() => {

              const queryParams = {
                finalize: true
              };

              const url = this.authData?.data?.product_id == Config.builder2PluginId ? ['/setup/builder-v2'] : ['/setup/ai-demo'];
              this._router.navigate((url), {
                queryParams: queryParams
              });
            }, 3000);
          }
        }
      });
  }

  public navigateToUrl(response: DbResponse): void {
    this._store.dispatch(new fromStore.LoadUser());
    const subscription: Subscription = this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() =>
          combineLatest(
            this._store.select(fromStore.getUser),
            this._store.select(fromStore.getUserSubscription),
            this._store.select(fromStore.getWebsitesLoaded)
              .pipe(
                filter((websitesLoaded: boolean) => websitesLoaded),
                switchMap(() =>
                  this._store.select(fromStore.getWebsites)
                ),
                first()
              )
          )
        )
      )
      .subscribe(([user, userSubscription, websites]) => {
        this.googleLoading = false;
        this.fromExperts && this._expertsService.redirectToExperts(this.fromExperts);
        this.userSubscription = userSubscription;
        this.websites = websites;
        this.loggedIn = response.data && TypeUtils.isDefined(response.data.logged_in) && !Utils.isTruthy(response.data.logged_in) ? response.data.logged_in  : 0;

        if ((!this.websites || (this.websites && !this.websites.length)) && this.userSubscription && this.userSubscription.subscriptionId === this.userSubscriptionIds.TRIAL_OPTIMIZER) {
          this._router.navigate(['/add-website'], { queryParams: {
              subscr_id: this.subscriptionId,
              boosterConnect: this._pluginConnectService.pluginData && this._pluginConnectService.pluginData.pluginName === '10webspeedoptimizer'
            } });
        }
        else if ((!this.websites || (this.websites && !this.websites.length)) && this.userSubscription && (this.userSubscription.type === UserSubscriptionType.ONE_PRODUCT || this.userSubscription.type === UserSubscriptionType.PREMIUM)) {
          this._router.navigate(['/download-plugins'], { queryParams: {
              subscr_id: this.subscriptionId
            } });
        }
        else {
          if (this._route.snapshot.queryParams['navigate_to']) {
            const url = !user.signUpFor ? '/setup' : `/${this._route.snapshot.queryParams['navigate_to']}`;

            this._router.navigate([url], {queryParams: this._route.snapshot.queryParams});
          }
          else if (this.subscriptionId) {
            this._router.navigate(['/websites'], {
              queryParams: {
                subscr_id: this.subscriptionId,
                plugin_slug: this.pluginSlug,
                logged_in: this.loggedIn,
                firstTime: true
              }
            });
          }
          else {
            this._router.navigate([this._route.snapshot.queryParams['returnUrl'] || '/'], {
              queryParams: {
                plugin_slug: this.pluginSlug,
                logged_in: this.loggedIn
              }
            });
          }
        }
        subscription.unsubscribe();
      });
  }

  public authLogin(recaptchaToken): void {
    this._authService.login(this.loginForm.value, false, recaptchaToken, false, this.authKeyFromGoogle)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe((response) => {
        if (response.success) {
          if (response.data && response.data['requires_2fa']) {
            this._2faVerificationStep = true;
          }
          else {
            this.navigateToUrl(response);
          }
        }
        else {
          this.loading = false;
          if (response.serverErrors && response.serverErrors.message !== "Client is not verified") {
            this.serverErrors = response.serverErrors;
            this.checkHasServerFieldErrors(response.serverErrors);
          }
          else {
            this.userNotVerified = true;
          }
        }
      });
  }

  private loginRequest() {
    this.loginWithGoogle = false;
    if (this.enableRecaptcha) {
      this.reCaptchaV3Service.execute(Config.googleRecaptcha, 'signup', (token) => {
        this.authLogin(token);
      });
    }
    else {
      this.authLogin(null);
    }
  }

  public togglePassword() {
    this.showPassword = !this.showPassword;
  }

  private initLoginFromPlugin(): void {
    if (!this._pluginConnectService.pluginData) return;
  }

  private initLoginFromZendesk(queryParams: any): void {
    this._isLoginFromZendesk = !!queryParams.zendesk;
  }

  public loginWith2fa(formDataFrom2fa) {
    this.twoFaToken = formDataFrom2fa && formDataFrom2fa['login2faCode'];
    this.twoFaToken && this.loginForm.addControl(
      'login2faCode',
      new FormControl(this.twoFaToken)
    );
    if (this.loginWithGoogle) {
      this.verifyToken();
    }
    else {
      this.loginRequest();
    }
  }

  public verifyToken(googleToken = null): void {
    const token = googleToken || this.googleToken;

    if (token) {
      this.googleLoading = true;
      this.googleToken = token;
      this._checkoutService.verifyGoogleToken(this.googleToken, this.subscriptionId, this._pluginConnectService.pluginData, null, null, null, null, this.twoFaToken)
        .pipe(filter((response: DbResponse): boolean => !!response),
          first())
        .subscribe((response) => {
          if (response.success) {
            if (response.data && response.data['requires_2fa']) {
              this.loginWithGoogle = true;
              this._2faVerificationStep = true;
            }
            else {
              if (response.data && response.data.agreement) {
              }
              if (response.data && response.data.email && response.data.auth_key) {
                this.loginForm.get('email').setValue(response.data.email);
                this.authKeyFromGoogle = response.data.auth_key;
                this.googleLoading = false;
              }
              else {
                this._globalEventDispatcher.dispatchEvent(GlobalEvent.userLogIn);
                this._store.dispatch(new fromStore.UserLogIn);
                this.navigateToUrl(response);
              }
            }
          }
          else {
            if (response.serverErrors && response.serverErrors.message !== "Client is not verified") {
              this.serverErrors = response.serverErrors;
              this.checkHasServerFieldErrors(response.serverErrors);
            }
            else {
              this.userNotVerified = true;
            }
            this.googleLoading = false;
          }
        });
    }
  }

  public onLoginSubmit(): void {
    this.formSubmitted = true;

    if (this.loginForm.valid) {
      this.loading = true;
      this.serverErrors = null;
      this.loginRequest();
    }
  }

  public recaptchaMode(): void {
    this._authService.recaptchaEnv()
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response) => {
        if (response.success) {
          this.enableRecaptcha = response.data.recaptcha_mode.value === 'live';
        }});
  }

  private loginFromMagicLink(token, email, googleRecaptcha = null): void {
    const data = {
      "magic_token": token,
      "email": email
    };
    this._authService.login(data, false, googleRecaptcha)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response) => {
        if (response.success) {
          this.navigateToUrl(response);
        }
        else {
          this.loading = false;
          this.loadingGoogle = false;
          if (response.serverErrors && response.serverErrors.message !== "Client is not verified") {
            this.serverErrors = response.serverErrors;
            this.checkHasServerFieldErrors(response.serverErrors);
          }
          else {
            this.userNotVerified = true;
          }
        }
      });
  }

  private sendToDisclaimer(google = false) {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUser))
      )
      .subscribe((user: User) => {
        if (user) {
          if (google) {
            this._router.navigate(['/add-website'], {
              queryParams: {
                fromGoogleSignIn: true,
                subscr_id: this.authData.data.subscription_id
              }
            });
          }
          else {
            this._router.navigate(['/websites']);
          }
        }

      });
  }

  private checkHasServerFieldErrors(serverErrors: DbServerErrors): void {
    this.hasServerFieldErrors = !!Utils.intersect(Object.keys(serverErrors.fields), ['name', 'email', 'password', 'stripe_token', 'agreement_error']).length;
  }

  /* submit on enter key press */
  keyDownFunction(event) {
    if (event.keyCode == 13) {
      this.onLoginSubmit();
    }
  }
  private acceptWorkspaceInvitation() {
    this._userManagementService.acceptInvitation(this.invitationData.workspace_invitation_id)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response) => {
        if (response.success && response.response.status === 200) {
          this._store.dispatch(new fromStore.LoadUser());
        }
        else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
      });
  }

  public resetLoginPage() {
    this.authKeyFromGoogle = false;
    this.googleError = null;
    this.formSubmitted = false;
    this.serverErrors = null;
    this.loginForm.get('email').reset();
    this.loginForm.get('password').reset();
  }

  public redirectToSignUp(): void {
    const queryParams = {};
    if (this._pluginConnectService.pluginData) {
      queryParams['site_url'] = this._pluginConnectService.pluginData.siteUrl;
      queryParams['return_url'] = this._pluginConnectService.pluginData.returnUrl;
      queryParams['nonce'] = this._pluginConnectService.pluginData.nonce;
      queryParams['subscr_id'] = this._pluginConnectService.pluginData.pluginName !== '10webplugin' ? Config.speedStarterPlanId : Config.basePlanId;
    } else if (this.fromExperts) {
      queryParams['plugin_id'] = 200;
      queryParams['fromExperts'] = this.fromExperts;
    }
    this._router.navigate(['/sign-up'], {queryParams});
  }

  private _setCompanyInfoFromRoute(): void {
    this.companyInfo = this._route.snapshot.data['companyInfo'];
  }

  private _sendGaEvent(action): void {
    const analyticsData = {
      eventCategory: 'Onboarding',
      eventAction: action,
      eventLabel: 'Log in',
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCoreWithoutUserData(analyticsData);
  }

  public sendMagicLink() {
    this.loadingMagicLink = true;
    this._userService.sendMagicLinkEmailPublic(this.emailFromMail, this.magicLinkPath)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe((response) => {
        this.loadingMagicLink = false;
        if (response.success) {
          NotifierComponent.notify(new Notification('Magic link sent to your email', NotificationType.SUCCESS));
        }
      });
  }
}
